"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import Alert from "@/app/_components/ui/Alert2";
import { RefreshCw, WifiOff } from "lucide-react";

type NetworkAlert = {
  key: number;
  message: string;
  type: "success" | "error";
};

const NETWORK_CHECK_URLS = [
  process.env.NEXT_PUBLIC_NETWORK_CHECK_URL,
  "https://www.gstatic.com/generate_204",
  "https://clients3.google.com/generate_204",
].filter((url): url is string => Boolean(url));

export default function GlobalNetworkStatus() {
  const t = useTranslations("networkStatus");
  const [networkAlert, setNetworkAlert] = useState<NetworkAlert | null>(null);
  const [isOffline, setIsOffline] = useState(false);
  const hasInitialized = useRef(false);
  const alertKeyRef = useRef(0);
  const currentStatusRef = useRef<boolean | null>(null);
  const checkingRef = useRef(false);
  const checkNetworkStatusRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const notifyStatusChange = (online: boolean) => {
      if (currentStatusRef.current === online) return;

      currentStatusRef.current = online;
      setIsOffline(!online);

      if (hasInitialized.current) {
        alertKeyRef.current += 1;
        setNetworkAlert({
          key: alertKeyRef.current,
          message: online
            ? t("connectionRestored")
            : t("connectionLost"),
          type: online ? "success" : "error",
        });
        return;
      }

      hasInitialized.current = true;
    };

    const checkNetworkStatus = async () => {
      if (checkingRef.current) return;

      if (!window.navigator.onLine) {
        notifyStatusChange(false);
        return;
      }

      checkingRef.current = true;

      try {
        let reachable = false;

        for (const url of NETWORK_CHECK_URLS) {
          const controller = new AbortController();
          const timeoutId = window.setTimeout(() => controller.abort(), 3000);

          try {
            await fetch(`${url}?network-check=${Date.now()}`, {
              method: "GET",
              mode: "no-cors",
              cache: "no-store",
              signal: controller.signal,
            });

            reachable = true;
            break;
          } catch {
            // Try the next endpoint before deciding the network is down.
          } finally {
            window.clearTimeout(timeoutId);
          }
        }

        notifyStatusChange(reachable);
      } catch {
        notifyStatusChange(false);
      } finally {
        checkingRef.current = false;
      }
    };

    checkNetworkStatusRef.current = () => {
      void checkNetworkStatus();
    };

    checkNetworkStatus();

    const handleOnline = () => {
      void checkNetworkStatus();
    };

    const handleOffline = () => {
      notifyStatusChange(false);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkNetworkStatus();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("focus", handleOnline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const intervalId = window.setInterval(() => {
      void checkNetworkStatus();
    }, 5000);

    return () => {
      checkNetworkStatusRef.current = null;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("focus", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <>
      {networkAlert ? (
        <div className="pointer-events-none fixed right-4 top-4 z-[2000] w-full max-w-sm px-4 sm:px-0">
          <div className="pointer-events-auto">
            <Alert
              key={networkAlert.key}
              type={networkAlert.type}
              message={networkAlert.message}
              duration={4000}
              onClose={() => setNetworkAlert(null)}
            />
          </div>
        </div>
      ) : null}

      {isOffline ? (
        <button
          type="button"
          onClick={() => checkNetworkStatusRef.current?.()}
          className="fixed bottom-4 right-4 z-[1990] inline-flex items-center gap-2 rounded-full border border-red-300 bg-red-500 px-4 py-3 text-sm font-medium text-white shadow-lg transition hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300"
          aria-label={t("offlineButtonAriaLabel")}
        >
          <WifiOff className="h-4 w-4" />
          <span>{t("offline")}</span>
          <RefreshCw className="h-4 w-4" />
        </button>
      ) : null}
    </>
  );
}
